import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import DatePicker from "react-datepicker";
import moment from "moment";

import "./DatePickerModalStyles.scss";
import "./styles.scss";
import ClearIcon from "../../../Icon/Clear";
import Calendar from "../../../Icon/Calendar";
import CustomHeader from "./CustomHeader";

const delay = (callback, delay = 200) => {
  setTimeout(() => {
    callback && callback();
  }, delay);
};

const getDayClassName = date => {
  const isToday =
    moment().format("MMM Do YY") === moment(date).format("MMM Do YY");
  return classNames("day", { today: isToday });
};

const getFormatWeekDay = nameOfDay => nameOfDay.substr(0, 1);

const CustomDatePicker = ({
  label,
  meta,
  containerStyle,
  disabled,
  outputDateFormat,
  dateFormat,
  input,
  inputRef,
  className,
  clearable,
  onDatePickerChange,
  onClear,
  convertOutputDateToString,
  active,
  getDatesRange,
  relatedDate,
  ...inputProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentDate = useMemo(() => moment());
  const currentDay = useMemo(() => currentDate.day());
  const convertDate = useCallback(
    date =>
      convertOutputDateToString ? moment(date).format(dateFormat) : date,
    [convertOutputDateToString, dateFormat]
  );
  const convertInputValueToDate = useCallback(
    value =>
      moment(value, dateFormat, true).isValid()
        ? moment(value, dateFormat).toDate()
        : null,
    [dateFormat]
  );
  const [selectedValue, setSelectedValue] = useState(
    convertInputValueToDate(input.value)
  );
  const datePickerRef = useRef(null);

  const [datesRange, setDatesRange] = useState(getDatesRange());
  const updateDates = useCallback(() => {
    const inputDate = convertInputValueToDate(input.value);
    const newDatesRang = getDatesRange();
    setDatesRange(newDatesRang);

    // weird behavior of the react-datepicker - change selectedValue
    if (moment(inputDate).isSameOrBefore(newDatesRang.minDate, "day")) {
      input.onChange(convertDate(newDatesRang.minDate));
      setSelectedValue(newDatesRang.minDate);
    } else {
      setSelectedValue(inputDate);
    }
  }, [
    input.value,
    input.onChange,
    getDatesRange,
    convertInputValueToDate,
    setDatesRange,
    setSelectedValue,
  ]);

  // NOTE: change date in the end of day
  useEffect(() => {
    const msTillEndOfDay = moment()
      .endOf("day")
      .add(1, "seconds")
      .diff(moment(), "milliseconds");
    const timer = setTimeout(() => updateDates(), msTillEndOfDay);

    return () => clearTimeout(timer);
  }, []);

  // NOTE: manage manual date change (currentDay is required)
  useEffect(() => updateDates(), [currentDay, input.value, updateDates]);

  useEffect(() => {
    if (inputRef) {
      if (datePickerRef.current) {
        inputRef.current = datePickerRef.current.input;
        return;
      }
      inputRef.current = datePickerRef.current;
    }
  }, [datePickerRef.current]);

  useEffect(() => {
    onDatePickerChange(isOpen);
  }, [isOpen]);

  const onSelect = useCallback(
    date => {
      setIsOpen(false);
      input.onChange(convertDate(date));
      delay(input.onBlur);
    },
    [input, dateFormat, convertOutputDateToString]
  );

  const onCalendarOpen = () => {
    datePickerRef.current.handleBlur = e => {
      datePickerRef.current.props.onBlur(e);
    };
  };

  const onFocus = useCallback(() => {
    // Prevent redundant focus trigger on click on input (weird behavior of the react-datepicker)
    if (!active) {
      setIsOpen(true);
      delay(input.onFocus);
      datePickerRef.current.handleBlur = e => {
        datePickerRef.current.props.onBlur(e);
      };
    }
  }, [input, active, setIsOpen]);

  const onChangeRaw = useCallback(
    event => {
      input.onChange(event.target.value);
      event.preventDefault();
    },
    [input]
  );

  const onClickOutside = useCallback(
    event => {
      // Prevent click outside trigger on input click (weird behavior of the react-datepicker)
      if (
        datePickerRef?.current &&
        datePickerRef?.current?.input?.id !== event?.target?.id
      ) {
        setIsOpen(false);
        delay(input.onBlur);
      }
    },
    [input, setIsOpen]
  );

  const handleClear = useCallback(() => {
    input.onChange(null);
    setSelectedValue(null);
    onClear && onClear();
  }, [input, setSelectedValue, onClear]);

  const onKeyDown = event => {
    // Close the calendar when user using 'Tab' key to tab off the custom input
    // it's react-datepicker bug https://github.com/Hacker0x01/react-datepicker/issues/1595
    event.which === 9 && setIsOpen(false);
  };

  return (
    <div
      className={classNames("mydpd-date-container", className, containerStyle)}
    >
      <DatePicker
        {...inputProps}
        className={classNames("input", meta.error && meta.touched && "invalid")}
        ref={datePickerRef}
        open={isOpen}
        disabled={disabled}
        disabledKeyboardNavigation
        customInput={
          <input
            ref={inputRef}
            className="mydpd-input mydpd-form-control form-control"
          />
        }
        maxLength={10}
        selected={selectedValue}
        formatWeekDay={getFormatWeekDay}
        dayClassName={getDayClassName}
        showDisabledMonthNavigation
        showPopperArrow={false}
        onSelect={onSelect}
        onCalendarOpen={onCalendarOpen}
        // Blur action is delegated to onClickOutside
        onBlur={onClickOutside}
        onFocus={onFocus}
        onChangeRaw={onChangeRaw}
        onClickOutside={onClickOutside}
        renderCustomHeader={params => (
          <CustomHeader
            {...params}
            currentDate={currentDate}
            relatedDate={relatedDate}
          />
        )}
        readOnly
        onKeyDown={onKeyDown}
        minDate={datesRange.minDate}
        maxDate={datesRange.maxDate}
      />
      {clearable && !!selectedValue ? (
        <div
          className={classNames("icon icon-clear", disabled && "icon_disabled")}
          onClick={handleClear}
        >
          <ClearIcon />
        </div>
      ) : (
        <div
          className={classNames("icon", disabled && "icon_disabled")}
          onMouseDown={() => !disabled && setIsOpen(!isOpen)}
        >
          <Calendar />
        </div>
      )}
    </div>
  );
};

export const propTypes = {
  label: PropTypes.string,
  meta: PropTypes.object,
  relatedDate: PropTypes.object,
  tooltipPosition: PropTypes.string,
  containerStyle: PropTypes.object,
  className: PropTypes.string,
  outputDateFormat: PropTypes.string,
  dateFormat: PropTypes.string,
  input: PropTypes.object,
  clearable: PropTypes.bool,
  onDatePickerChange: PropTypes.func,
  getDatesRange: PropTypes.func,
  convertOutputDateToString: PropTypes.bool,
  ...DatePicker.PropTypes,
};

CustomDatePicker.propTypes = propTypes;

CustomDatePicker.defaultProps = {
  convertOutputDateToString: true,
  outputDateFormat: "DD/MM/YYYY",
  dateFormat: "dddd DD/MM/YYYY",
  meta: {},
  onDatePickerChange: () => {},
  onClear: () => {},
  getDatesRange: () => ({ minDate: null, maxDate: null }),
};

export default CustomDatePicker;
